import { AloftDateJSON, CompType, PropertyData, ZillowPropertySearchResponse } from '@aloft/toolkit-shared';

import { useAutofillProperty } from '../../../hooks/useAutofillProperty.ts';
import { CompSelectionState } from '../types.ts';
import { getZillowCompRecID } from './getZillowCompRecID.ts';
import { zillowPropToPropertyData } from './zillowPropToPropertyData.ts';

export async function handleAddClickZillowCraftsman({
  data,
  isLoading,
  rowSelection,
  createComps,
  autofillProperty,
  effectiveDate,
  orderID,
  isInspectifyOrder,
  hasArvOption,
}: {
  data: ZillowPropertySearchResponse[];
  isLoading: boolean;
  rowSelection: CompSelectionState;
  createComps: (comps: PropertyData[]) => void;
  autofillProperty: ReturnType<typeof useAutofillProperty>;
  effectiveDate: AloftDateJSON;
  orderID: string;
  isInspectifyOrder: boolean;
  hasArvOption: boolean;
}) {
  const listingToProp = zillowPropToPropertyData(autofillProperty, effectiveDate, orderID);

  // Note: our Zillow query only returns properties of the same type as the subject,
  // so using the subject's property type for all comps
  if (!isLoading) {
    if (isInspectifyOrder && hasArvOption) {
      // New behavior: create separate comps for each selected comp type (AIV/ARV)
      const properties: Promise<PropertyData>[] = data
        .filter((c) => c.address && c.dateSold)
        .map((c) => ({
          listing: c,
          id: getZillowCompRecID(c),
        }))
        .filter(({ id }) => {
          const selection = rowSelection[id ?? ''];
          return selection && (selection.aiv || selection.arv);
        })
        .flatMap(({ id, listing }) => {
          const selection = rowSelection[id ?? ''];
          if (!selection) return [];

          const compTypes: CompType[] = [];
          if (selection.aiv) compTypes.push('aiv');
          if (selection.arv) compTypes.push('arv');

          // Create a separate PropertyData for each comp type selected
          return compTypes.map(async (compType) => {
            const property = await listingToProp(listing);
            return { ...property, compType };
          });
        });

      createComps(await Promise.all(properties));
    } else {
      // Old behavior: create comps without compType for non-Inspectify orders or Inspectify orders without ARV option
      const properties: Promise<PropertyData>[] = data
        .filter((c) => c.address && c.dateSold)
        .map((c) => ({
          listing: c,
          id: getZillowCompRecID(c),
        }))
        .filter(({ id }) => id && rowSelection[id])
        .map(async ({ listing }) => listingToProp(listing));

      createComps(await Promise.all(properties));
    }
  }
}
